/**
 * 앱 상단 헤더 컴포넌트
 *
 * - 로고 + 시스템명 "잔류물질안전정보시스템"
 * - 언어 토글 (한국어/English) - useLanguage 훅 사용
 * - 로그인/로그아웃 버튼 - Redux auth 상태 사용
 * - 모바일 햄버거 메뉴 버튼
 * - 알림 벨 아이콘 (/admin/notifications 링크)
 *
 * 디자인: 짙은 블루(#001529) 배경, 밝은 텍스트/아이콘
 */
import React, { useState, useMemo } from "react";
import { Layout, Button, Space, Badge, Drawer, message } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  BellOutlined,
  GlobalOutlined,
  BulbOutlined,
  BulbFilled,
  LoginOutlined,
  LogoutOutlined,
  UserOutlined,
  SafetyOutlined,
  RightOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  selectIsLoggedIn,
  selectUser,
  showLoginModal,
  logout,
  selectDevRole,
  setDevRole,
} from "../../store/authSlice";
import { useUnreadNotificationCount } from "../../hooks/queries/useAdmin";
import useLanguage from "../../hooks/useLanguage";
import useTheme, { DARK_MODE_ENABLED } from "../../hooks/useTheme";
import useResponsive from "../../hooks/useResponsive";
import useAuth from "../../hooks/useAuth";
import { buildNavGroups, filterNavGroups } from "../../constants/navGroups";

const { Header } = Layout;

// ─── 다국어 텍스트 정의 ─────────────────────────────────────
const TEXTS = {
  ko: {
    systemName: "식품의약품안전처 잔류물질정보",
    login: "로그인",
    logout: "로그아웃",
    korean: "한국어",
    english: "English",
    menu: "메뉴",
    // ── 네비 그룹 레이블 (HomeNavBar와 동기화) ──
    prd: "농약",
    prdInfo: "농약정보",
    mrl: "잔류허용기준",
    food: "식품",
    adi: "ADI",
    using: "사용현황",
    analysis: "표준품",
    analysisLegacy: "표준품(구)",
    reports: "보고서",
    progress: "수입식품 중 농약잔류허용기준 설정진행사항",
    prdUsageInfo: "농약 사용정보",
    vdUsageInfo: "동약 사용정보",
    vd: "동물용의약품",
    vdInfo: "동물용의약품정보",
    std: "표준품",
    standards: "표준품 현황",
    applications: "표준품 신청",
    requests: "기준요청",
    companies: "업체관리",
    statuses: "현황",
    units: "단위",
    doc: "게시판",
    articles: "공지/자료",
    relatedSites: "관련기관",
    prdBoard: "농약 게시판",
    vdBoard: "동물용의약품 게시판",
    // ── 관리자 메뉴 ──
    admin: "관리자",
    dashboard: "대시보드",
    users: "사용자 관리",
    authTemplates: "권한 템플릿",
    visitors: "방문자 현황",
    audit: "감사 로그",
    workflow: "워크플로우",
    notifications: "공지사항",
    batch: "배치 작업",
    quality: "품질 관리",
    popups: "팝업 관리",
    // admin accordion 그룹 레이블
    adminAuth: "인증 관리",
    adminBase: "공통 마스터 관리",
    adminPrdFood: "농약 식품 관리",
    adminVdFood: "동약 식품 관리",
    adminSystem: "시스템 관리",
    // 기준 정보 sub-items
    masterCompanies: "제조회사",
    masterUnits: "단위",
    masterStatuses: "상태",
    masterCountries: "국가",
    masterProvinces: "소속",
    // 식품 sub-items
    foodClassLarge: "식품 대분류",
    foodClassMiddle: "식품 중분류",
    foodClassSmall: "식품 소분류",
    foodMaster: "식품 마스터",
    // 시스템 관리 sub-items
    systemMaintenance: "유지보수 이력",
    // ── 기타 ──
    darkMode: "다크",
    lightMode: "라이트",
    noMenuAvailable: "접근 가능한 메뉴가 없습니다.",
  },
  en: {
    systemName: "Residue Safety Information System",
    login: "Login",
    logout: "Logout",
    korean: "한국어",
    english: "English",
    menu: "Menu",
    // ── 네비 그룹 레이블 ──
    prd: "Pesticides",
    prdInfo: "Pesticide Info",
    mrl: "MRL",
    food: "Food",
    adi: "ADI",
    using: "Usage",
    analysis: "Analysis",
    analysisLegacy: "Analysis (Legacy)",
    reports: "Reports",
    progress: "MRL Progress for Imported Foods",
    prdUsageInfo: "Pesticide Usage Info",
    vdUsageInfo: "Vet Drug Usage Info",
    vd: "Vet. Drugs",
    vdInfo: "Vet Drug Info",
    std: "Standard",
    standards: "Standard Status",
    applications: "Standard Request",
    requests: "Requests",
    companies: "Companies",
    statuses: "Status",
    units: "Units",
    doc: "Board",
    articles: "Articles",
    relatedSites: "Related Sites",
    prdBoard: "Pesticide Board",
    vdBoard: "Vet Drug Board",
    // ── Admin ──
    admin: "Admin",
    dashboard: "Dashboard",
    users: "User Mgmt",
    authTemplates: "Auth Templates",
    visitors: "Visitors",
    audit: "Audit Log",
    workflow: "Workflow",
    notifications: "Notifications",
    batch: "Batch Jobs",
    quality: "Quality",
    popups: "Popups",
    adminAuth: "Authentication",
    adminBase: "Common Master Data",
    adminPrdFood: "PRD Food",
    adminVdFood: "VD Food",
    adminSystem: "System Mgmt",
    masterCompanies: "Companies",
    masterUnits: "Units",
    masterStatuses: "Statuses",
    masterCountries: "Countries",
    masterProvinces: "Provinces",
    foodClassLarge: "Food Class (Large)",
    foodClassMiddle: "Food Class (Middle)",
    foodClassSmall: "Food Class (Small)",
    foodMaster: "Food Master",
    systemMaintenance: "Maintenance Log",
    darkMode: "Dark",
    lightMode: "Light",
    noMenuAvailable: "No accessible menu items.",
  },
};

// ─── 스타일 상수 ─────────────────────────────────────────────
const HEADER_BG = "#001529";
const HEADER_HEIGHT = 64;
const GRADIENT_LINE_DARK =
  "linear-gradient(90deg, #0d4a7a 0%, #1a6e6e 50%, #0d4a7a 100%)";

// 밝은 버튼 공통 스타일 (어두운 배경 위)
const lightButtonStyle = {
  color: "rgba(255, 255, 255, 0.85)",
  border: "none",
  transition: "all 0.3s ease",
};

/**
 * 앱 상단 헤더
 * @param {Object} props
 * @param {Function} props.onToggleSidebar - 사이드바 토글 핸들러
 * @param {boolean} props.sidebarCollapsed - 사이드바 접힘 상태
 * @param {boolean} props.showSidebarToggle - 햄버거 버튼 표시 여부 (홈에서는 false)
 */
const AppHeader = ({
  onToggleSidebar,
  sidebarCollapsed,
  showSidebarToggle = true,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const user = useSelector(selectUser);
  const devRole = useSelector(selectDevRole);
  const { isMobile } = useResponsive();
  const { t, language, setLanguage } = useLanguage(TEXTS);
  const { isDark, toggleTheme } = useTheme();
  // 권한 기반 메뉴 필터링용 (모바일 Drawer)
  const { hasPermission } = useAuth();

  // 로고 이미지 로드 실패 시 대체 아이콘 표시
  const [logoError, setLogoError] = useState(false);

  // 모바일 네비 Drawer
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 모바일 Drawer에서 accordion 펼침 상태 (한 번에 하나만 열리는 exclusive accordion)
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);

  // 네비 그룹 — 공통 모듈 사용 (HomeNavBar와 동일 소스/필터링 규칙)
  const filteredNavGroups = useMemo(() => {
    const groups = buildNavGroups(t);
    return filterNavGroups(groups, user, hasPermission);
  }, [t, user, hasPermission]);

  /** accordion 토글 (HomeNavBar 동일 패턴) */
  const toggleSubmenu = (key, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setExpandedSubmenu((prev) => (prev === key ? null : key));
  };

  /** Drawer 아이템 클릭 핸들러 (내부 링크) */
  const handleNavItemClick = (path) => {
    setDrawerVisible(false);
    history.push(path);
  };

  /** Drawer 아이템 클릭 핸들러 (외부 링크) */
  const handleExternalClick = (url) => {
    window.open(url, '_blank', 'noopener');
  };

  // 관리자 여부 — 알림 API는 /admin/notifications 하위라 admin 전용
  // 일반 사용자가 호출 시 400/403 오류가 나므로 쿼리 자체를 비활성화한다.
  const isAdminAccount = String(user?.USERID || '').trim() === 'admin';

  // 읽지 않은 알림 수 (관리자 로그인 시에만 조회)
  const { data: unreadCount } = useUnreadNotificationCount({
    enabled: isLoggedIn && isAdminAccount,
  });

  /** 로그인 모달 열기 */
  const handleLogin = () => {
    dispatch(showLoginModal());
  };

  /** 로그아웃 처리 */
  const handleLogout = async () => {
    await dispatch(logout());
    message.success('로그아웃되었습니다.');
    history.push('/');
  };

  /** 알림 페이지 이동 */
  const handleNotificationClick = () => {
    history.push("/admin/notifications");
  };

  /** 언어 토글 (한↔영 직접 전환) */
  const handleToggleLanguage = () => {
    setLanguage(language === 'ko' ? 'en' : 'ko');
  };

  return (
    <Header
      style={{
        background: HEADER_BG,
        padding: 0,
        borderBottom: "none",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: 100,
        height: HEADER_HEIGHT,
        animation: "headerFadeIn 0.25s ease both",
      }}
    >
      {/* ── 내부 컨테이너: HomeNavBar와 동일 1200px 중앙 정렬 ── */}
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: isMobile ? "0 12px" : "0 8px",
          height: HEADER_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* ── 좌측: 햄버거 메뉴(기능 페이지만) + 로고 + 시스템명 ── */}
        <Space align="center">
          {/* 사이드바 토글 - 홈에서는 숨김 */}
          {showSidebarToggle && (
            <Button
              type="text"
              icon={
                sidebarCollapsed ? (
                  <MenuUnfoldOutlined style={{ color: "#fff" }} />
                ) : (
                  <MenuFoldOutlined style={{ color: "#fff" }} />
                )
              }
              onClick={onToggleSidebar}
              className="app-header-btn"
              style={{ ...lightButtonStyle, fontSize: 18 }}
            />
          )}

          {/* 로고 + 시스템명 */}
          <div
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              transition: "opacity 0.3s ease",
            }}
            onClick={() => history.push("/")}
          >
            {/* 로고 이미지 또는 대체 아이콘 */}
            {logoError ? (
              <SafetyOutlined
                style={{
                  fontSize: 28,
                  color: "#36cfc9",
                  marginRight: 10,
                }}
              />
            ) : (
              <img
                src="/img/govern_logo.png"
                alt="로고"
                style={{ height: 32, marginRight: 10 }}
                onError={() => setLogoError(true)}
              />
            )}

            {/* {!isMobile && ( */}
            <span
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "#ffffff",
                whiteSpace: "nowrap",
                letterSpacing: "0.5px",
              }}
            >
              {t.systemName}
            </span>
            {/* // ) */}
            {/* // } */}
          </div>
        </Space>

        {/* ── 우측: 알림, 언어, 로그인/로그아웃 ── */}
        <Space size={isMobile ? 8 : 16}>
          {/* 알림 벨 (관리자 로그인 시에만 표시) */}
          {isLoggedIn && isAdminAccount && (
            <Badge count={unreadCount || 0} size="small">
              <Button
                type="text"
                icon={
                  <BellOutlined
                    style={{ fontSize: 18, color: "rgba(255,255,255,0.85)" }}
                  />
                }
                onClick={handleNotificationClick}
                className="app-header-btn"
                style={lightButtonStyle}
              />
            </Badge>
          )}

          {/* 언어 토글 (모바일에서는 하단 네비에 있으므로 숨김) */}
          {!isMobile && (
            <Button
              type="text"
              icon={<GlobalOutlined />}
              onClick={handleToggleLanguage}
              className="app-plain-btn"
            >
              <span>{language === "ko" ? "한국어" : "EN"}</span>
            </Button>
          )}

          {/* 다크 모드 토글 — DARK_MODE_ENABLED(useTheme.js) 켜기 전까지 숨김 */}
          {!isMobile && DARK_MODE_ENABLED && (
            <Button
              type="text"
              icon={isDark ? <BulbFilled /> : <BulbOutlined />}
              onClick={toggleTheme}
              className="app-plain-btn"
            >
              <span>{isDark ? t.lightMode : t.darkMode}</span>
            </Button>
          )}

          {/* 개발용 역할 토글 — 실제 로그인 구현으로 비활성화
              REACT_APP_SHOW_DEV_ROLE=true 환경변수가 있을 때만 표시 */}
          {!isMobile && process.env.REACT_APP_SHOW_DEV_ROLE === 'true' && (() => {
            const ROLE_CYCLE = ['guest', 'normal', 'admin'];
            const ROLE_LABEL = { guest: '비회원', normal: '일반회원', admin: '관리자' };
            const ROLE_BG = {
              guest: 'rgba(255,255,255,0.05)',
              normal: 'rgba(24,144,255,0.25)',
              admin: 'rgba(82,196,26,0.25)',
            };
            const ROLE_BORDER = {
              guest: '1px solid rgba(255,255,255,0.15)',
              normal: '1px solid rgba(24,144,255,0.5)',
              admin: '1px solid rgba(82,196,26,0.5)',
            };
            const currentIdx = ROLE_CYCLE.indexOf(devRole);
            const nextRole = ROLE_CYCLE[(currentIdx + 1) % ROLE_CYCLE.length];
            return (
              <Button
                type="text"
                icon={<SafetyOutlined />}
                onClick={() => {
                  dispatch(setDevRole(nextRole));
                  window.location.reload();
                }}
                className="app-plain-btn"
                style={{
                  ...lightButtonStyle,
                  background: ROLE_BG[devRole] || ROLE_BG.guest,
                  border: ROLE_BORDER[devRole] || ROLE_BORDER.guest,
                  borderRadius: 6,
                }}
                title="개발용: 권한 순환 (비회원 → 일반회원 → 관리자)"
              >
                <span>{ROLE_LABEL[devRole] || '비회원'}</span>
              </Button>
            );
          })()}

          {/* 로그인/로그아웃 버튼 (모바일에서는 하단 네비에 있으므로 숨김) */}
          {!isMobile && (
            isLoggedIn ? (
              <Space>
                <span style={{ color: "rgba(255, 255, 255, 0.65)" }}>
                  <UserOutlined
                    style={{ marginRight: 4, color: "rgba(255,255,255,0.65)" }}
                  />
                  {user?.name || user?.userId}
                </span>
                <Button
                  type="text"
                  icon={<LogoutOutlined />}
                  onClick={handleLogout}
                  className="app-plain-btn"
                >
                  {t.logout}
                </Button>
              </Space>
            ) : (
              <Button
                type="text"
                icon={<LoginOutlined />}
                onClick={handleLogin}
                className="app-plain-btn"
              >
                {t.login}
              </Button>
            )
          )}

          {/* 모바일 햄버거 메뉴 버튼 */}
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: 20, color: "#fff" }} />}
              onClick={() => setDrawerVisible(true)}
              className="app-header-btn"
              style={lightButtonStyle}
            />
          )}
        </Space>
      </div>

      {/* ── 모바일 네비 Drawer ── */}
      <Drawer
        title={t.menu}
        placement="right"
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={320}
        bodyStyle={{ padding: 0, background: "#f8fbfa" }}
        headerStyle={{
          background: "linear-gradient(135deg, #065A4A, #0A7C66)",
          borderBottom: "none",
          padding: "20px 20px 16px",
        }}
        className="mobile-nav-drawer"
      >
        {/* 네비게이션 콘텐츠 */}
        <div style={{ padding: "12px" }}>
          {filteredNavGroups.length === 0 && (
            <div
              style={{
                padding: "24px 16px",
                textAlign: "center",
                color: "#888",
                fontSize: 13,
              }}
            >
              {t.noMenuAvailable}
            </div>
          )}
          {filteredNavGroups.map((group) => (
            <div
              key={group.key}
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid rgba(10,124,102,0.1)",
                marginBottom: 10,
                overflow: "hidden",
              }}
            >
              {/* 섹션 헤더 */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 16px 10px",
                  borderBottom: "1px solid rgba(10,124,102,0.08)",
                  background: "#f8fbfa",
                }}
              >
                <div
                  style={{
                    width: 4,
                    height: 18,
                    borderRadius: 2,
                    background: "linear-gradient(180deg, #0A7C66, #2F6DB3)",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#065A4A",
                  }}
                >
                  {group.label}
                </span>
              </div>

              {/* 메뉴 아이템 — accordion 부모(children), external, 일반 링크 3종 처리 */}
              {group.items.map((item, idx) => {
                const isLast = idx === group.items.length - 1;

                // (1) accordion 부모: children 보유
                if (item.children) {
                  const isExpanded = expandedSubmenu === item.key;
                  return (
                    <React.Fragment key={item.key}>
                      <div
                        className="mobile-nav-item mobile-nav-accordion-parent"
                        onClick={(e) => toggleSubmenu(item.key, e)}
                        style={{
                          padding: "11px 16px 11px 30px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: isExpanded ? "#0A7C66" : "#444",
                          background: isExpanded ? "#eef8f5" : "transparent",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderBottom:
                            isLast && !isExpanded
                              ? "none"
                              : "1px solid #f0f0f0",
                        }}
                      >
                        <span>{item.label}</span>
                        {isExpanded ? (
                          <MinusOutlined
                            style={{ fontSize: 11, color: "#0A7C66" }}
                          />
                        ) : (
                          <PlusOutlined
                            style={{ fontSize: 11, color: "#888" }}
                          />
                        )}
                      </div>
                      {/* 자식 아이템 — 펼침 시에만 표시 */}
                      {isExpanded && item.children.map((child, cIdx) => {
                        const isLastChild = cIdx === item.children.length - 1;
                        const onClick = child.external
                          ? () => handleExternalClick(child.key)
                          : () => handleNavItemClick(child.key);
                        return (
                          <div
                            key={child.key}
                            className="mobile-nav-item mobile-nav-sub-item"
                            onClick={onClick}
                            style={{
                              padding: "10px 16px 10px 48px",
                              fontSize: 12.5,
                              color: "#555",
                              background: "#f8fbfa",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderBottom:
                                isLast && isLastChild
                                  ? "none"
                                  : "1px solid #eef2f1",
                            }}
                          >
                            <span>{child.label}</span>
                            <RightOutlined
                              style={{ fontSize: 9, color: "#ccc" }}
                            />
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                }

                // (2) external 링크
                if (item.external) {
                  return (
                    <div
                      key={item.key}
                      className="mobile-nav-item"
                      onClick={() => handleExternalClick(item.key)}
                      style={{
                        padding: "11px 16px 11px 30px",
                        fontSize: 13,
                        color: "#444",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: isLast ? "none" : "1px solid #f0f0f0",
                      }}
                    >
                      <span>{item.label}</span>
                      <RightOutlined style={{ fontSize: 9, color: "#ccc" }} />
                    </div>
                  );
                }

                // (3) 일반 내부 링크
                return (
                  <div
                    key={item.key}
                    className="mobile-nav-item"
                    onClick={() => handleNavItemClick(item.key)}
                    style={{
                      padding: "11px 16px 11px 30px",
                      fontSize: 13,
                      color: "#444",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: isLast ? "none" : "1px solid #f0f0f0",
                    }}
                  >
                    <span>{item.label}</span>
                    <RightOutlined style={{ fontSize: 9, color: "#ccc" }} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Drawer>

      {/* ── 그라데이션 바 + 별똥별 파티클 ── */}
      <div className="header-grad-bar">
        <div className="header-shooting-star" />
      </div>

      {/* ── 인라인 스타일 ── */}
      <style>{`
        @keyframes headerFadeIn { from { opacity: 0; } to { opacity: 1; } }

        /* ── 그라데이션 바 ── */
        .header-grad-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: ${GRADIENT_LINE_DARK};
          overflow: hidden;
        }

        /* ── 흰색 스윕 (진행바 느낌) ── */
        @keyframes headerSweep {
          0%   { left: -40%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { left: 140%; opacity: 0; }
        }
        .header-shooting-star {
          position: absolute;
          top: 0;
          left: -40%;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 20%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.15) 80%, transparent 100%);
          animation: headerSweep 3s ease-in-out infinite;
        }
        .app-header-btn:hover {
          background: rgba(255, 255, 255, 0.12) !important;
          color: #fff !important;
        }
        .app-plain-btn {
          background: transparent !important;
          color: rgba(255, 255, 255, 0.85) !important;
          border: none !important;
          box-shadow: none !important;
          display: inline-flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 6px !important;
        }
        .app-plain-btn > .anticon + span,
        .app-plain-btn > span + span {
          margin-left: 0 !important;
        }
        .app-plain-btn:hover {
          background: rgba(255, 255, 255, 0.12) !important;
          color: #fff !important;
        }
        /* 모바일 네비 Drawer */
        .mobile-nav-drawer .ant-drawer-header {
          background: linear-gradient(135deg, #065A4A, #0A7C66) !important;
          border-bottom: none !important;
          padding: 20px 20px 16px !important;
        }
        .mobile-nav-drawer .ant-drawer-title {
          color: #fff !important;
          font-size: 18px !important;
          font-weight: 700 !important;
        }
        .mobile-nav-drawer .ant-drawer-close {
          color: rgba(255,255,255,0.7) !important;
        }
        .mobile-nav-drawer .ant-drawer-close:hover {
          color: #fff !important;
        }
        .mobile-nav-drawer .ant-drawer-body {
          background: #f8fbfa !important;
        }
        .mobile-nav-item {
          transition: background 0.15s, color 0.15s;
        }
        .mobile-nav-item span:first-child {
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: keep-all;
          line-height: 1.3;
        }
        .mobile-nav-item:hover {
          background: #E8F5F0 !important;
          color: #0A7C66 !important;
        }
        .mobile-nav-item:active {
          background: #d5ede5 !important;
        }
        .mobile-nav-accordion-parent:hover {
          background: #eef8f5 !important;
        }
        .mobile-nav-sub-item:hover {
          background: #e0f2ec !important;
          color: #0A7C66 !important;
        }
      `}</style>
    </Header>
  );
};

export default AppHeader;
